import React from "react";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { Edit, Trash2 } from "lucide-react";

const GRID_COLUMNS = "minmax(180px, 1.6fr) 140px 140px 96px";

function formatDuration(value) {
  if (value === null || value === undefined || value === "" || value === "—") {
    return "—";
  }
  return value;
}

export function isTrainingProgramModulesListTable(tableName) {
  return String(tableName || "").toLowerCase() === "ems_training_program_modules";
}

export function getTrainingProgramModuleGroupRows(row) {
  const modules = Array.isArray(row?.modules) ? row.modules : [];
  return modules.map((mod, index) => {
    const id =
      mod?.organization_ems_training_program_module_id ??
      mod?.id ??
      `${row?.id || "module"}-${index}`;
    return {
      key: String(id),
      id,
      organization_ems_training_program_module_id: id,
      module_name: mod?.module_name || "-",
      duration_in_hours: formatDuration(mod?.duration_in_hours),
      duration_in_days: formatDuration(mod?.duration_in_days),
    };
  });
}

export function TrainingProgramModulesAccordionCollapseRow({
  expanded,
  colSpan,
  accordionRows,
  colors,
  showEditButton = false,
  showDeleteButton = false,
  onEdit,
  onDelete,
}) {
  return (
    <TableRow>
      <TableCell
        colSpan={colSpan}
        sx={{
          py: 0,
          px: 2,
          borderBottom: `1px solid ${colors.grey[200]}`,
          backgroundColor: colors.grey[50],
        }}
      >
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ py: 1.5, pl: "56px" }}>
            {showEditButton && accordionRows[0] ? (
              <Box sx={{ display: "flex", justifyContent: "flex-end", pb: 1, pr: 1 }}>
                <Tooltip title="Edit all modules together">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit?.(accordionRows[0]);
                    }}
                    sx={{
                      color: colors.text.primary,
                      border: `1px solid ${colors.grey[300]}`,
                      borderRadius: "8px",
                      px: 1,
                    }}
                  >
                    <Edit size={15} />
                    <Box sx={{ ml: 0.75, fontSize: "12.5px", fontWeight: 700 }}>
                      Edit all
                    </Box>
                  </IconButton>
                </Tooltip>
              </Box>
            ) : null}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: GRID_COLUMNS,
                gap: 1,
                pr: 1,
                pb: 1,
                fontSize: "12px",
                fontWeight: 700,
                color: colors.text.secondary,
              }}
            >
              <Box>Module Name</Box>
              <Box>Duration (Hours)</Box>
              <Box>Duration (Days)</Box>
              <Box sx={{ textAlign: "right" }}>Actions</Box>
            </Box>

            {accordionRows.length === 0 ? (
              <Typography
                variant="body2"
                sx={{ px: 1, color: colors.text.secondary }}
              >
                No modules in this program.
              </Typography>
            ) : (
              accordionRows.map((detail) => (
                <Box
                  key={detail.key}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: GRID_COLUMNS,
                    alignItems: "center",
                    gap: 1,
                    pr: 1,
                    py: 1,
                    fontSize: "13px",
                    borderTop: `1px dashed ${colors.grey[300]}`,
                  }}
                >
                  <Box sx={{ fontWeight: 600, color: colors.text.primary }}>
                    {detail.module_name}
                  </Box>
                  <Box>{detail.duration_in_hours}</Box>
                  <Box>{detail.duration_in_days}</Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 0.25,
                    }}
                  >
                    {showEditButton && (
                      <Tooltip title="Edit all modules">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEdit?.(detail);
                          }}
                          sx={{ color: colors.text.primary }}
                        >
                          <Edit size={15} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {showDeleteButton && (
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDelete?.(detail);
                          }}
                          sx={{ color: colors.text.primary }}
                        >
                          <Trash2 size={15} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              ))
            )}
          </Box>
        </Collapse>
      </TableCell>
    </TableRow>
  );
}
